/**
 * BESTIX v5.0 — API Client
 * Type-safe fetch wrapper for Bespokian Events API
 */

import { z } from 'zod';

// ============================================
// API Configuration
// ============================================

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:7781';
const STOREFRONT_SLUG = import.meta.env.VITE_STOREFRONT_SLUG ?? 'concerts';
const DEFAULT_TIMEOUT = 30000;

// ============================================
// Error Types
// ============================================

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(message: string = 'Network request failed') {
    super(message);
    this.name = 'NetworkError';
  }
}

// ============================================
// Auth Token Management
// ============================================

let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

// ============================================
// Response Schemas - Events API
// ============================================

export const VenueSchema = z.object({
  id: z.number(),
  name: z.string(),
  city: z.string(),
  state: z.string(),
});

export const PerformerSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: z.string().optional(),
});

export const PriceRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
  currency: z.string(),
});

export const EventSchema = z.object({
  id: z.number(),
  name: z.string(),
  date: z.string(),
  time: z.string(),
  venue: VenueSchema,
  performers: z.array(PerformerSchema),
  category: z.string().optional(),
  imageUrl: z.string().optional(),
  priceRange: PriceRangeSchema.optional(),
  ticketsAvailable: z.boolean(),
});

export const SeatsSchema = z.object({
  low: z.string(),
  high: z.string(),
}).optional();

export const TicketPriceSchema = z.object({
  wholesale: z.number(),
  retail: z.number().optional(),
  face: z.number().optional(),
  currency: z.string(),
});

export const TicketSchema = z.object({
  id: z.number(),
  eventId: z.number(),
  section: z.string(),
  row: z.string(),
  seats: SeatsSchema,
  availableQuantity: z.number(),
  purchasableQuantities: z.array(z.number()),
  price: TicketPriceSchema,
  deliveryMethods: z.array(z.string()),
  isInstantDelivery: z.boolean(),
  isETicket: z.boolean(),
  notes: z.string().optional(),
});

export const EventsSearchResponseSchema = z.object({
  events: z.array(EventSchema),
  totalCount: z.number().optional(),
  page: z.number(),
  perPage: z.number(),
});

export const EventDetailResponseSchema = z.object({
  event: EventSchema,
  tickets: z.array(TicketSchema),
  totalTickets: z.number(),
});

export const PaymentIntentResponseSchema = z.object({
  client_secret: z.string(),
  payment_intent_id: z.string(),
  lock_id: z.string(),
  lock_expires_at: z.string().optional(),
  amount: z.number(),
  currency: z.string(),
});

export const BookingResponseSchema = z.object({
  order_id: z.string(),
  mercury_transaction_id: z.number(),
  delivery_method: z.string(),
  total_amount: z.number(),
  currency: z.string(),
});

export const ETicketResponseSchema = z.object({
  type: z.enum(['eticket', 'barcode', 'transfer']),
  eticket: z.object({
    pdf_base64: z.string(),
    media_type: z.string(),
  }).optional(),
  barcodes: z.array(z.object({
    seat_number: z.number(),
    barcode: z.string(),
    barcode_type: z.string(),
  })).optional(),
  transfer_urls: z.array(z.string()).optional(),
});

// Categories (supports hierarchical structure from /categories/top)
export interface Category {
  path: string;
  name: string;
  depth: number;
  parentPath: string;
  eventCount?: number;
  children?: Category[];
}

export const CategorySchema: z.ZodType<Category> = z.lazy(() =>
  z.object({
    path: z.string(),
    name: z.string(),
    depth: z.number(),
    parentPath: z.string(),
    eventCount: z.number().optional(),
    children: z.array(CategorySchema).optional(),
  })
);

export const CategoriesResponseSchema = z.object({
  categories: z.array(CategorySchema),
  totalCount: z.number().optional(),
  page: z.number().optional(),
  perPage: z.number().optional(),
});

// Venue Search
export const VenueSearchResultSchema = z.object({
  id: z.number(),
  name: z.string(),
  city: z.string().optional(),
  state: z.string().optional(),
});

export const VenueSearchResponseSchema = z.object({
  venues: z.array(VenueSearchResultSchema),
});

// Performer Search
export const PerformerSearchResultSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: z.string().optional(),
});

export const PerformerSearchResponseSchema = z.object({
  performers: z.array(PerformerSearchResultSchema),
});

// ============================================
// Auth Schemas
// ============================================

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(['user', 'admin']).optional().transform((r) => r ?? 'user'),
});

export type User = {
  id: string;
  email: string;
  name: string | null;
  role: 'user' | 'admin';
};

export const AuthResponseSchema = z.object({
  token: z.string(),
  user: UserSchema,
});

// ============================================
// User Profile Schemas
// ============================================

export const UserPreferencesSchema = z.object({
  emailNotifications: z.boolean().optional(),
  smsNotifications: z.boolean().optional(),
  pushNotifications: z.boolean().optional(),
  marketingEmails: z.boolean().optional(),
  currency: z.string().optional(),
  timezone: z.string().optional(),
  language: z.string().optional(),
});

export const UserProfileSchema = z.object({
  id: z.string(),
  email: z.string(),
  emailVerified: z.boolean().optional(),
  phone: z.string().nullable().optional(),
  phoneVerified: z.boolean().optional(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  role: z.string(),
  kycStatus: z.string().optional(),
  accountStatus: z.string().optional(),
  preferences: UserPreferencesSchema.optional(),
  createdAt: z.string().optional(),
  lastLoginAt: z.string().nullable().optional(),
});

export const UserAddressSchema = z.object({
  id: z.string(),
  label: z.string().nullable().optional(),
  line1: z.string(),
  line2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string(),
  isDefault: z.boolean().optional(),
  isVerified: z.boolean().optional(),
  createdAt: z.string().optional(),
});

// ============================================
// Order Schemas
// ============================================

export const OrderItemSchema = z.object({
  id: z.string(),
  listingId: z.string().optional(),
  eventId: z.union([z.string(), z.number()]),
  eventName: z.string(),
  eventDate: z.string(),
  venueName: z.string(),
  section: z.string(),
  row: z.string().nullable().optional(),
  seatNumbers: z.array(z.string()).optional(),
  quantity: z.number(),
  unitPriceCents: z.number(),
  totalPriceCents: z.number(),
  status: z.string(),
  sellerId: z.string().optional(),
  sellerName: z.string().optional(),
  deliveryMethod: z.string().optional(),
  tickets: z.array(z.unknown()).optional(),
  fulfilledAt: z.string().nullable().optional(),
  cancelledAt: z.string().nullable().optional(),
  refundedAt: z.string().nullable().optional(),
});

export const OrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  userId: z.string().optional(),
  status: z.enum(['pending', 'confirmed', 'fulfilled', 'cancelled', 'refunded']),
  items: z.array(OrderItemSchema),
  subtotalCents: z.number(),
  serviceFeesCents: z.number().optional(),
  taxCents: z.number().optional(),
  totalCents: z.number(),
  currency: z.string(),
  paymentIntentId: z.string().optional(),
  paymentStatus: z.string().optional(),
  billingAddress: z.unknown().optional(),
  shippingAddress: z.unknown().nullable().optional(),
  deliveryPreferences: z.unknown().optional(),
  metadata: z.unknown().optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  confirmedAt: z.string().nullable().optional(),
  fulfilledAt: z.string().nullable().optional(),
  cancelledAt: z.string().nullable().optional(),
  refundedAt: z.string().nullable().optional(),
});

export const OrdersListResponseSchema = z.object({
  orders: z.array(OrderSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  hasMore: z.boolean(),
});

// ============================================
// Type Exports
// ============================================

export type Venue = z.infer<typeof VenueSchema>;
export type Performer = z.infer<typeof PerformerSchema>;
export type PriceRange = z.infer<typeof PriceRangeSchema>;
export type Event = z.infer<typeof EventSchema>;
export type Ticket = z.infer<typeof TicketSchema>;
export type EventsSearchResponse = z.infer<typeof EventsSearchResponseSchema>;
export type EventDetailResponse = z.infer<typeof EventDetailResponseSchema>;
export type PaymentIntentResponse = z.infer<typeof PaymentIntentResponseSchema>;
export type BookingResponse = z.infer<typeof BookingResponseSchema>;
export type ETicketResponse = z.infer<typeof ETicketResponseSchema>;
// Category is defined as interface above (supports recursive children)
export type CategoriesResponse = z.infer<typeof CategoriesResponseSchema>;
export type VenueSearchResult = z.infer<typeof VenueSearchResultSchema>;
export type VenueSearchResponse = z.infer<typeof VenueSearchResponseSchema>;
export type PerformerSearchResult = z.infer<typeof PerformerSearchResultSchema>;
export type PerformerSearchResponse = z.infer<typeof PerformerSearchResponseSchema>;
export type AuthResponse = z.output<typeof AuthResponseSchema>;
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;
export type UserProfile = z.infer<typeof UserProfileSchema>;
export type UserAddress = z.infer<typeof UserAddressSchema>;
export type OrderItem = z.infer<typeof OrderItemSchema>;
export type Order = z.infer<typeof OrderSchema>;
export type OrdersListResponse = z.infer<typeof OrdersListResponseSchema>;

// ============================================
// Request Helpers
// ============================================

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
  requiresAuth?: boolean;
  isEventsEndpoint?: boolean;
}

async function request<T>(
  endpoint: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {}
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    timeout = DEFAULT_TIMEOUT,
    requiresAuth = false,
    isEventsEndpoint = false,
  } = options;

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...headers,
  };

  // Add X-Storefront header for events endpoints
  if (isEventsEndpoint) {
    requestHeaders['X-Storefront'] = STOREFRONT_SLUG;
  }

  // Add auth token if required
  if (requiresAuth && authToken) {
    requestHeaders['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      credentials: 'include',
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.error || `Request failed with status ${response.status}`,
        response.status
      );
    }

    const data = await response.json();
    const validated = schema.safeParse(data);

    if (!validated.success) {
      if (import.meta.env.DEV) {
        console.error('Response validation failed', {
          url,
          endpoint,
          issues: validated.error.issues,
          data,
        });
      }
      // In production, return data as-is if validation fails but data exists
      return data as T;
    }

    return validated.data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new NetworkError('Request timed out');
    }

    throw new NetworkError(
      error instanceof Error ? error.message : 'Unknown error occurred'
    );
  }
}

// ============================================
// API Methods
// ============================================

export const api = {
  // Auth
  auth: {
    login: (email: string, password: string) =>
      request('/auth/login', AuthResponseSchema, {
        method: 'POST',
        body: { email, password },
      }),

    register: (data: { email: string; password: string; name?: string }) =>
      request('/auth/register', AuthResponseSchema, {
        method: 'POST',
        body: {
          email: data.email,
          password: data.password,
          ...(data.name && { name: data.name }),
        },
      }),

    me: () =>
      request('/auth/me', UserSchema, {
        requiresAuth: true,
      }),

    logout: (refreshToken?: string, allDevices?: boolean) =>
      request('/auth/logout', z.object({ message: z.string() }), {
        method: 'POST',
        body: { refreshToken, allDevices },
        requiresAuth: true,
      }),
  },

  // Users
  users: {
    getProfile: () =>
      request('/users/me', UserProfileSchema, {
        requiresAuth: true,
      }),

    updateProfile: (data: {
      firstName?: string;
      lastName?: string;
      displayName?: string | null;
      phone?: string;
    }) =>
      request('/users/me', UserProfileSchema, {
        method: 'PUT',
        body: data,
        requiresAuth: true,
      }),

    updatePreferences: (preferences: Partial<UserPreferences>) =>
      request('/users/me/preferences', UserPreferencesSchema, {
        method: 'PUT',
        body: preferences,
        requiresAuth: true,
      }),

    getAddresses: () =>
      request('/users/me/addresses', z.array(UserAddressSchema), {
        requiresAuth: true,
      }),

    addAddress: (address: {
      label?: string;
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postalCode: string;
      country?: string;
      isDefault?: boolean;
    }) =>
      request('/users/me/addresses', UserAddressSchema, {
        method: 'POST',
        body: address,
        requiresAuth: true,
      }),
  },

  // Orders
  orders: {
    list: (params?: {
      status?: 'pending' | 'confirmed' | 'fulfilled' | 'cancelled' | 'refunded';
      limit?: number;
      offset?: number;
      sortBy?: 'createdAt' | 'updatedAt';
      sortOrder?: 'asc' | 'desc';
    }) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.set(key, String(value));
          }
        });
      }
      const query = searchParams.toString();
      return request(
        `/orders${query ? `?${query}` : ''}`,
        OrdersListResponseSchema,
        { requiresAuth: true }
      );
    },

    getById: (id: string) =>
      request(`/orders/${id}`, OrderSchema, {
        requiresAuth: true,
      }),

    cancel: (id: string, reason: string, idempotencyKey: string) =>
      request(`/orders/${id}/cancel`, z.object({
        order: OrderSchema,
        refund: z.unknown().nullable(),
      }), {
        method: 'POST',
        body: { reason, idempotencyKey },
        requiresAuth: true,
      }),

    getTickets: (id: string) =>
      request(`/orders/${id}/tickets`, z.object({
        tickets: z.array(z.unknown()),
      }), {
        requiresAuth: true,
      }),
  },

  // Events
  events: {
    search: (params?: {
      q?: string;
      category?: string;
      performer_id?: number;
      venue_id?: number;
      start_date?: string;
      end_date?: string;
      min_price?: number;
      max_price?: number;
      page?: number;
      per_page?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.set(key, String(value));
          }
        });
      }
      const query = searchParams.toString();
      return request(
        `/events${query ? `?${query}` : ''}`,
        EventsSearchResponseSchema,
        { isEventsEndpoint: true }
      );
    },

    getById: (id: number, params?: {
      min_price?: number;
      max_price?: number;
      instant_download_only?: boolean;
      eticket_only?: boolean;
    }) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.set(key, String(value));
          }
        });
      }
      const query = searchParams.toString();
      return request(
        `/events/${id}${query ? `?${query}` : ''}`,
        EventDetailResponseSchema,
        { isEventsEndpoint: true }
      );
    },

    createPaymentIntent: (data: {
      ticket_group_id: number;
      quantity: number;
      event_id: number;
      customer_email?: string;
    }) =>
      request('/events/payment-intent', PaymentIntentResponseSchema, {
        method: 'POST',
        body: data,
        isEventsEndpoint: true,
      }),

    completeBooking: (data: {
      lock_id: string;
      payment_intent_id: string;
      delivery_method?: string;
      customer: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: {
          street1: string;
          street2?: string;
          city: string;
          state: string;
          zipCode: string;
          country?: string;
        };
      };
      notes?: string;
    }) =>
      request('/events/book', BookingResponseSchema, {
        method: 'POST',
        body: data,
        isEventsEndpoint: true,
        requiresAuth: true,
      }),

    getETickets: (mercuryTransactionId: number) =>
      request(
        `/events/orders/${mercuryTransactionId}/tickets`,
        ETicketResponseSchema,
        {
          isEventsEndpoint: true,
          requiresAuth: true,
        }
      ),

    getCategories: (params?: {
      name?: string;
      parent_path?: string;
      depth?: number;
      page?: number;
      per_page?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.set(key, String(value));
          }
        });
      }
      const query = searchParams.toString();
      return request(
        `/events/categories${query ? `?${query}` : ''}`,
        CategoriesResponseSchema,
        { isEventsEndpoint: true }
      );
    },

    /**
     * Get top-level categories for filtering
     * Returns hierarchical category structure with main categories and their children
     */
    getTopCategories: () => {
      return request(
        `/events/categories/top`,
        CategoriesResponseSchema,
        { isEventsEndpoint: true }
      );
    },

    /**
     * Search venues by name
     */
    searchVenues: (q: string) => {
      return request(
        `/events/venues/search?q=${encodeURIComponent(q)}`,
        VenueSearchResponseSchema,
        { isEventsEndpoint: true }
      );
    },

    /**
     * Search performers by name
     */
    searchPerformers: (q: string) => {
      return request(
        `/events/performers/search?q=${encodeURIComponent(q)}`,
        PerformerSearchResponseSchema,
        { isEventsEndpoint: true }
      );
    },
  },
};

export default api;
